import React, { useEffect, useMemo, useState } from 'react';
import { StepContainer, NavigationButtons, InfoBox, RadioGroup, FormField } from '../index';
import { ActivityBoundary } from '../../components/ActivityBoundary';
import { useWizard } from '../../wizard';
import { contentApi } from '../../services/contentApi';
import { DL_CUSTODY_TYPES, isDlCustodyType } from '../../constants/driver';
import type { DlCustodyType } from '../../constants/driver';
import { isValidEmail, normalizeWhitespace } from '../../utils/validation';
import { contentKeys } from '../../constants/contentKeys';

type FieldKey = 'dlGuardian1Name' | 'dlGuardian1Email' | 'dlGuardian2Name' | 'dlGuardian2Email';

type ErrorState = {
  under18?: string;
  custody?: string;
  guardian1Name?: string;
  guardian1Email?: string;
  guardian2Name?: string;
  guardian2Email?: string;
};

const fallbackCopy = [
  'Hvis du er under 18 år, skal begge dine forældremyndighedsindehavere underskrive samtykkeerklæringen (P23T).',
  'Vi sender en digital anmodning til de e-mailadresser, du angiver herunder.'
];

const custodyOptions = DL_CUSTODY_TYPES.map(option => ({ value: option.value as DlCustodyType, label: option.label }));

const DriverParentConsentStep: React.FC = () => {
  const { selfServiceData, updateSelfServiceData, goNext, goBack } = useWizard();
  const [errors, setErrors] = useState<ErrorState>({});
  const [copy, setCopy] = useState<string[] | null>(null);

  useEffect(() => {
    if (selfServiceData.dlParentConsentMode !== 'driver_flow') {
      updateSelfServiceData('dlParentConsentMode', 'driver_flow');
    }
  }, [selfServiceData.dlParentConsentMode, updateSelfServiceData]);

  const under18Value = useMemo(() => {
    if (selfServiceData.dlUnder18 === true) {return 'yes';}
    if (selfServiceData.dlUnder18 === false) {return 'no';}
    return '';
  }, [selfServiceData.dlUnder18]);

  const custodyValue = useMemo(() => selfServiceData.dlCustodyType ?? '', [selfServiceData.dlCustodyType]);
  const notifySecondGuardian = !!selfServiceData.dlNotifySecondGuardian;
  const showGuardianFields = under18Value === 'yes';
  const showSecondGuardian = showGuardianFields && custodyValue === 'med';

  useEffect(() => {
    contentApi.getByKey(contentKeys.koerekort.dl_parent_consent).then(content => {
      if (content?.body && content.body.length > 0) {
        setCopy(content.body);
      } else {
        setCopy(fallbackCopy);
      }
    });
  }, []);

  const handleUnder18Change = (value: string) => {
    const isUnder18 = value === 'yes';
    updateSelfServiceData('dlUnder18', isUnder18);
    updateSelfServiceData('dlParentConsentRequested', isUnder18);
    if (!isUnder18) {
      updateSelfServiceData('dlCustodyType', undefined);
      updateSelfServiceData('dlGuardian1Name', '');
      updateSelfServiceData('dlGuardian1Email', '');
      updateSelfServiceData('dlGuardian2Name', '');
      updateSelfServiceData('dlGuardian2Email', '');
      updateSelfServiceData('dlNotifySecondGuardian', false);
      updateSelfServiceData('dlGuardian1Signed', false);
      updateSelfServiceData('dlGuardian2Signed', false);
    }
    setErrors(prev => ({ ...prev, under18: undefined }));
  };

  const handleCustodySelection = (value: string) => {
    if (isDlCustodyType(value)) {
      updateSelfServiceData('dlCustodyType', value);
      if (value === 'ene') {
        updateSelfServiceData('dlGuardian2Name', '');
        updateSelfServiceData('dlGuardian2Email', '');
        updateSelfServiceData('dlNotifySecondGuardian', false);
        updateSelfServiceData('dlGuardian2Signed', false);
      }
      setErrors(prev => ({ ...prev, custody: undefined }));
    }
  };

  const handleFieldChange = (field: FieldKey, value: string) => {
    updateSelfServiceData(field, value);
    const errorKey: keyof ErrorState = field === 'dlGuardian1Name'
      ? 'guardian1Name'
      : field === 'dlGuardian1Email'
        ? 'guardian1Email'
        : field === 'dlGuardian2Name'
          ? 'guardian2Name'
          : 'guardian2Email';
    setErrors(prev => ({ ...prev, [errorKey]: undefined }));
  };

  const handleDigitalPostChange = (checked: boolean) => {
    updateSelfServiceData('dlNotifySecondGuardian', checked);
  };

  const validate = (): ErrorState => {
    const newErrors: ErrorState = {};

    if (!under18Value) {
      newErrors.under18 = 'Vælg om du er under 18 år.';
    }

    if (under18Value === 'yes') {
      if (!custodyValue) {
        newErrors.custody = 'Vælg forældremyndighed.';
      }

      const guardian1Name = normalizeWhitespace(selfServiceData.dlGuardian1Name ?? '');
      const guardian1Email = normalizeWhitespace(selfServiceData.dlGuardian1Email ?? '');

      if (!guardian1Name) {
        newErrors.guardian1Name = 'Angiv navn på forælder/værge 1.';
      }

      if (!guardian1Email) {
        newErrors.guardian1Email = 'Angiv e-mail til forælder/værge 1.';
      } else if (!isValidEmail(guardian1Email)) {
        newErrors.guardian1Email = 'Indtast en gyldig e-mailadresse.';
      }

      if (custodyValue === 'med') {
        const guardian2Name = normalizeWhitespace(selfServiceData.dlGuardian2Name ?? '');
        const guardian2Email = normalizeWhitespace(selfServiceData.dlGuardian2Email ?? '');

        if (!guardian2Name) {
          newErrors.guardian2Name = 'Angiv navn på forælder/værge 2.';
        }

        if (!guardian2Email) {
          newErrors.guardian2Email = 'Angiv e-mail til forælder/værge 2.';
        } else if (!isValidEmail(guardian2Email)) {
          newErrors.guardian2Email = 'Indtast en gyldig e-mailadresse.';
        }
      }
    }

    return newErrors;
  };

  const resetSignatureState = () => {
    updateSelfServiceData('dlGuardian1Signed', false);
    updateSelfServiceData('dlGuardian2Signed', false);
  };

  const handleNext = () => {
    const validation = validate();
    setErrors(validation);
    if (Object.keys(validation).length === 0) {
      resetSignatureState();
      goNext();
    }
  };



  const renderGuardianFields = (
    prefix: 'dlGuardian1' | 'dlGuardian2',
    sectionTitle: string,
    personLabel: string,
    errorNameKey: keyof ErrorState,
    errorEmailKey: keyof ErrorState
  ) => {
    const nameField = `${prefix}Name` as FieldKey;
    const emailField = `${prefix}Email` as FieldKey;
    const nameLabel = `Navn på ${personLabel}`;
    const emailLabel = `E-mail på ${personLabel}`;

    return (
      <div className="guardian-section">
        <h3>{sectionTitle}</h3>
        <div className="form-field-wrapper">
          <label className="input-label" htmlFor={nameField}>{nameLabel}</label>
          <FormField
            id={nameField}
            name={nameField}
            placeholder="Navn"
            value={selfServiceData[nameField] ?? ''}
            onChange={(event) => handleFieldChange(nameField, event.target.value)}
            error={errors[errorNameKey]}
            required
            autoComplete="name"
          />
        </div>
        <div className="form-field-wrapper">
          <label className="input-label" htmlFor={emailField}>{emailLabel}</label>
          <FormField
            id={emailField}
            type="email"
            name={emailField}
            placeholder="E-mail"
            value={selfServiceData[emailField] ?? ''}
            onChange={(event) => handleFieldChange(emailField, event.target.value)}
            error={errors[errorEmailKey]}
            required
            autoComplete="email"
          />
        </div>
      </div>
    );
  };
  return (
    <StepContainer title="Forældres samtykke">
      <InfoBox type="info">
        {(copy ?? fallbackCopy).map((paragraph, index) => (
          <p key={index}>{paragraph}</p>
        ))}
      </InfoBox>

      <RadioGroup
        name="dlUnder18"
        legend="Er du under 18 år?"
        value={under18Value}
        required
        options={[
          { value: 'yes', label: 'Ja, jeg er under 18 år' },
          { value: 'no', label: 'Nej, jeg er 18 år eller ældre' }
        ]}
        onChange={handleUnder18Change}
      />
      {errors.under18 && <span className="error-message" role="alert">{errors.under18}</span>}

      <ActivityBoundary mode={showGuardianFields ? 'visible' : 'hidden'}>
        <div className="guardian-form">
          <div className="guardian-section">
            <RadioGroup
              name="dlCustodyType"
              legend="Forældremyndighed"
              value={custodyValue}
              required
              options={custodyOptions}
              onChange={handleCustodySelection}
            />
            {errors.custody && <span className="error-message" role="alert">{errors.custody}</span>}
          </div>

          {renderGuardianFields('dlGuardian1', 'Oplysninger om forælder/værge 1', 'forælder/værge 1', 'guardian1Name', 'guardian1Email')}

          <ActivityBoundary mode={showSecondGuardian ? 'visible' : 'hidden'}>
            <>
              {renderGuardianFields('dlGuardian2', 'Oplysninger om forælder/værge 2', 'forælder/værge 2', 'guardian2Name', 'guardian2Email')}
              <label className="checkbox-field">
                <input
                  type="checkbox"
                  name="dlNotifySecondGuardian"
                  checked={notifySecondGuardian}
                  onChange={(event) => handleDigitalPostChange(event.target.checked)}
                />
                <span>Send også besked via Digital Post til forælder 2</span>
              </label>
            </>
          </ActivityBoundary>
        </div>
      </ActivityBoundary>

      <NavigationButtons onBack={goBack} onNext={handleNext} />
    </StepContainer>
  );
};

export default DriverParentConsentStep;


